// Vendor
import { warn } from "vue";
import type { Locale } from "vue-i18n";

/**
 * useLocalizedCityPath is a composable function that generates a localized path for a city based on a hotel slug or params object.
 *
 *
 * @returns A function that returns a localized path for a city based on a property slug of params object.
 * @example
 * ```ts
 * const localizedCityPath = useLocalizedCityPath();
 * const path = localizedCityPath("europa/paris/campos-eliseos-hotel", "es");
 * // path will be something like "/es/hoteles/europa/paris"
 * ```
 */
export default function useLocalizedCityPath() {
  const localePath = useLocalePath();

  /**
   * @param route - The route string or an object containing the continent and city.
   * @param locale - The locale to use for the path generation.
   */
  return (route: string | LocalizedCityParams, locale?: Locale) => {
    let continent: string | undefined;
    let city: string | undefined;

    if (typeof route === "string") {
      [continent, city] = route.split("/");
    } else {
      ({ continent, city } = route || {});
    }

    if (!continent) {
      warn(
        `useLocalizedCityPath: continent could not be resolved from route "${route}"`,
      );

      return null;
    }

    if (!city) {
      warn(
        `useLocalizedCityPath: city could not be resolved from route "${route}"`,
      );

      return null;
    }

    return localePath(
      {
        name: "hotels-continent-city",
        params: {
          continent,
          city,
        },
      },
      locale,
    );
  };
}
